import axios from "axios";
import { useEffect, useState } from "react";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

const Enquries = () => {
  const theme = useTheme();
  const [data, setData] = useState<any[]>([]);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [selectedEnquiry, setSelectedEnquiry] = useState("");
  const [selectedData, setSelectedData]: any = useState();
  const [remark, setRemark]: any = useState("");
  const [checked, setChecked] = useState(false);
  const [updated, setUpdated] = useState(false);

  const handleSelect = (id: any) => {
    setSelectedEnquiry(id);
    getDataOfSelectedEnqury(id);
    setUpdated(false);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
  };

  const getDataOfSelectedEnqury = async (id: any) => {
    try {
      const response: any = data?.find((item: any) => item?._id === id);
      setSelectedData(response);
      setRemark(response?.remark || "");
      if (response?.status === "Pending") {
        setChecked(false);
      } else {
        setChecked(true);
      }
    } catch (error) {
      console.log(error, "error ------->");
    }
  };

  const updateEnquiry = async () => {
    try {
      const response = await axios.post(
        `${ipconfig}/updateenquiry`,
        {
          enquiryId: selectedData?._id,
          remark: remark,
          status: checked ? "Completed" : "Pending",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setUpdated(true);
        toast.success("Enquiry updated.", {
          position: "top-center",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    } catch (error) {
      console.log(error, "Error ----->");
      toast.error("There is some error in updating.", {
        position: "top-center",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const handleEnquries = async () => {
    try {
      const statusParam = status === "All" ? "" : status;
      const typeParam = type === "All" ? "" : type;
      
      const queryParams: string[] = [];
      if (statusParam) queryParams.push(`status=${encodeURIComponent(statusParam)}`);
      if (typeParam) queryParams.push(`type=${encodeURIComponent(typeParam)}`);

      const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

      const res = await axios.get(`${ipconfig}/enquires${queryString}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const fetchedData = res?.data?.data || [];
      setData(fetchedData);

      // If currently selected enquiry is in fetched data, update selectedData
      if (selectedEnquiry) {
        const found = fetchedData.find((item: any) => item?._id === selectedEnquiry);
        if (found) {
          setSelectedData(found);
          setRemark(found?.remark || "");
          setChecked(found?.status === "Completed");
        }
      }
    } catch (error) {
      console.log(error, "Error");
    }
  };

  useEffect(() => {
    handleEnquries();
  }, [status, type, updated]);

  const renderTypeChip = (enquiryType?: string) => {
    const rawType = (enquiryType || "N/A").trim();
    const lowerType = rawType.toLowerCase();

    if (lowerType === "demo") {
      return (
        <Chip
          label="Demo"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "12px",
            backgroundColor: theme.palette.mode === "dark" ? "#1e1b4b" : "#e0e7ff",
            color: theme.palette.mode === "dark" ? "#a5b4fc" : "#4338ca",
            border: "1px solid",
            borderColor: theme.palette.mode === "dark" ? "#312e81" : "#c7d2fe",
          }}
        />
      );
    }

    if (lowerType === "download") {
      return (
        <Chip
          label="Download"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "12px",
            backgroundColor: theme.palette.mode === "dark" ? "#064e3b" : "#ecfdf5",
            color: theme.palette.mode === "dark" ? "#6ee7b7" : "#047857",
            border: "1px solid",
            borderColor: theme.palette.mode === "dark" ? "#065f46" : "#a7f3d0",
          }}
        />
      );
    }

    return (
      <Chip
        label={rawType}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: "12px",
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Filter Section */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          width: "100%",
        }}
      >
        <FormControl
          sx={{
            width: {
              xs: "100%",
              sm: "200px",
              lg: "240px",
            },
          }}
        >
          <InputLabel id="status-select-label">Status</InputLabel>
          <Select
            labelId="status-select-label"
            id="status-select"
            value={status}
            label="Status"
            onChange={handleStatusChange}
          >
            <MenuItem value="All">All Statuses</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          sx={{
            width: {
              xs: "100%",
              sm: "200px",
              lg: "240px",
            },
          }}
        >
          <InputLabel id="type-select-label">Type</InputLabel>
          <Select
            labelId="type-select-label"
            id="type-select"
            value={type}
            label="Type"
            onChange={handleTypeChange}
          >
            <MenuItem value="All">All Types</MenuItem>
            <MenuItem value="Demo">Demo</MenuItem>
            <MenuItem value="Download">Download</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          justifyContent: "flex-start",
          alignItems: "flex-start",
          width: "100%",
          height: "100%",
          gap: "24px",
        }}
      >
        {/* Enquiries List */}
        <Box
          sx={{
            flexGrow: 0,
            overflowY: "auto",
            height: {
              xs: "50vh",
              sm: "75vh",
              md: "75vh",
            },
            width: {
              xs: "100%",
              sm: "420px",
            },
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          {data?.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", pr: "8px" }}>
              {data?.map((item: any, index: any) => {
                const isSelected = selectedEnquiry === item?._id;
                const displayName = item?.companyName?.trim() || item?.name?.trim() || "No Company Name";
                return (
                  <Box
                    key={item?._id || index}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      backgroundColor: isSelected
                        ? theme.palette.mode === "light"
                          ? "#f0f7ff"
                          : "#1e3a8a33"
                        : theme.palette.mode === "light"
                        ? "background.paper"
                        : "#1e293b50",
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      borderRadius: "12px",
                      padding: "20px",
                      cursor: "pointer",
                      width: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow:
                          theme.palette.mode === "light"
                            ? "0 4px 12px rgba(0,0,0,0.05)"
                            : "0 4px 12px rgba(0,0,0,0.3)",
                      },
                    }}
                    onClick={() => handleSelect(item?._id)}
                  >
                    <Box sx={{ flexGrow: 1, pr: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                        {displayName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Contact: {item?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Phone: {item?.number || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Email: {item?.email || "N/A"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                        Date: {item?.enquiryDate ? moment(item.enquiryDate).format("DD-MM-YYYY hh:mm A") : "N/A"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
                      {item?.status === "Pending" ? (
                        <Typography sx={{ color: "error.main", fontWeight: 600, fontSize: "13px" }}>
                          Pending
                        </Typography>
                      ) : (
                        <Typography sx={{ color: "success.main", fontWeight: 600, fontSize: "13px" }}>
                          Completed
                        </Typography>
                      )}
                      {renderTypeChip(item?.type)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ padding: "20px", textAlign: "center", color: "text.secondary" }}>
              No enquiries found.
            </Box>
          )}
        </Box>

        {/* Selected Enquiry Detail View */}
        {selectedEnquiry !== "" && selectedData && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
              padding: "24px",
              gap: "20px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Company: {selectedData?.companyName?.trim() || selectedData?.name?.trim() || "N/A"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                    Type:
                  </Typography>
                  {renderTypeChip(selectedData?.type)}
                </Box>
              </Box>

              <Typography sx={{ color: "text.secondary" }}>
                <strong>Name:</strong> {selectedData?.name || "N/A"}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                <strong>Number:</strong> {selectedData?.number || "N/A"}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                <strong>Email:</strong> {selectedData?.email || "N/A"}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                <strong>Date:</strong>{" "}
                {selectedData?.enquiryDate ? moment(selectedData.enquiryDate).format("DD-MM-YYYY hh:mm A") : "N/A"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={(e: any) => setChecked(e.target.checked)}
                  inputProps={{ "aria-label": "enquiry status checkbox" }}
                />
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  Mark : {checked ? "Completed" : "Pending"}
                </Typography>
              </Box>

              <TextField
                label="Remark"
                multiline
                rows={6}
                placeholder="Enter your remark here"
                value={remark}
                onChange={(event: any) => setRemark(event.target.value)}
                fullWidth
              />

              <Button
                onClick={updateEnquiry}
                variant="contained"
                sx={{
                  width: "100%",
                  alignSelf: "center",
                  borderRadius: "10px",
                  backgroundColor: "#0E75A1",
                  textTransform: "none",
                  ":hover": {
                    backgroundColor: "#0b5d80",
                  },
                  marginTop: "20px",
                  fontWeight: 600,
                }}
                disabled={selectedData?.status === "Completed"}
              >
                Update
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <ToastContainer style={{ width: "auto" }} />
    </Box>
  );
};

export default Enquries;
